// R91 — **THE READ THAT SAYS WHAT SHE MAY OPEN, ASKED THREE TIMES IN THREE DIFFERENT WAYS, AND THE
// ONE THAT WAS RIGHT STATES ITS REASONING IN A COMMENT THE OTHER TWO NEVER READ.**
//
// `/api/membership/entitlements` has three call sites in App.tsx (R47's count-the-copies, where the
// COUNT is the finding):
//
//   1. `readLocks`        — `r.ok ? r.json() : null` -> `if (active && d)` with no else, `.catch(() => {})`
//   2. `requireFeature`   — **ALREADY HONEST**, and its own comment is the ruling: *"A network that
//                            cannot answer must not become a free pass to a paid room: the server
//                            would refuse the request anyway, so the honest local answer is the box,
//                            not the surface."*
//   3. `useEntitlements`  — does not use R20's law at all. It seeds state with a **FABRICATED
//                            PAYLOAD** (`{ tier: "free", owner: false, freeReportTypes: [],
//                            features: {}, purchases: [] }`) and keeps it on every failure.
//
// **R20's LAW IS NOT WHAT IS WRONG HERE, AND THAT IS THE POINT (R53/R85/R87).** `featureEntitlements`
// already answers `null` for a body that did not arrive, and `featureOutcome` already rules that
// NOT ARRIVED means `pending` and WAITS — because answering "no" would flash the tier box at a paying
// Vet on a slow network. That ruling is correct. **What nobody ever made true is the premise under
// it: that the answer eventually ARRIVES.** `readLocks` drops a failure on the floor and never asks
// again, so `featureEnt` stays `pending` **forever** — and W1-1's bounce effect opens with
// `if (!isLoggedIn || !featureEnt) return;`. **"Pending waits" is a correct ruling about a read that
// is still coming. It becomes a defect the moment nothing is coming.**
//
// **WHAT THE FORGED PAYLOAD COSTS, PER CONSUMER, AND TWO OF THE THREE ARE MONEY.**
//
//   • `freeReportTypes` -> `[]`, so `generateReport` routes EVERY report type through the membership
//     check. **That is the exact bug T1-FIX's own comment records the owner hitting live** — her own
//     signup Birth Chart, her Numerology and Human Design on its free first tap are hers at any tier,
//     and a failed read asks a free member to pay for the report that is already hers.
//   • `purchases` -> `[]`, so a one-time product she HAS BOUGHT wears the BUY face and she is invited
//     to pay a second time for something she owns. `features` -> `{}`, so `canStreamVideo` is false
//     and every video card draws a padlock for a member entitled to watch them. 14-B7d(3) fixed that
//     shelf's padlock in the OTHER direction; this puts one back on the whole shelf.
//   • `tier` -> `"free"`, so the money page reads a paying member as a stranger — **R87's finding one
//     route over, on the same fact, and the reason that round exists.**
//
// **THE FIX IS THE ONE THIS HOUSE HAS SHIPPED FORTY TIMES: ONE TRANSPORT, R18's DISTINCTION, AND A
// BOUNDED RE-ASK** (R31/R60 — the crossing stops existing rather than being watched). `doFetch` and
// `sleep` are PARAMETERS (R46), so a gate drives attempt three of three with no browser and no clock.
//
// **TWO ANSWERS, NOT THREE, ARGUED RATHER THAN COPIED (R58/R70):** the route is one line —
// `sendJson(response, 200, await resolveEntitlements(readSignedSession(request)))` — so it answers 200
// on its one path and composes no refusal at all. A signed-out visitor gets a real free payload, which
// is an ANSWER. There is no `sign-in` kind and no `refused` kind, because a branch for an answer a
// route cannot give is a second answer key.

export type EntitlementsPayload = {
  tier?: string;
  owner?: boolean;
  freeReportTypes?: string[];
  features?: Record<string, boolean>;
  purchases?: string[];
  locks?: Record<string, unknown>;
};

export type EntitlementsAnswer =
  | { kind: "payload"; payload: EntitlementsPayload }
  | { kind: "unreachable" };

// ONE round trip, and the answer it gives is about THIS attempt only. `readEntitlements` below owns
// how many attempts there are; keeping the two apart is what lets a gate drive a single bad answer
// without a clock.
export async function readEntitlementsOnce(doFetch: typeof fetch = fetch): Promise<EntitlementsAnswer> {
  try {
    const res = await doFetch("/api/membership/entitlements", { cache: "no-store" });
    // **A NON-200 IS NOT AN ANSWER THIS ROUTE GIVES.** Its only `sendJson` is a 200, so a 500 is
    // index.js's last-resort handler (a raw error message and no shape at all — R89) and a 502 is the
    // proxy in front of an instance that is restarting. Neither is "she is entitled to nothing".
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return { kind: "unreachable" }; }
    const data = (await res.json().catch(() => null)) as EntitlementsPayload | null;
    // A body that will not parse is transport noise wearing a 200 — a proxy's HTML error page is the
    // common one. R20's `featureEntitlements` rules a malformed body `null` for the same reason; this
    // keeps that judgement in one place rather than making a second one here.
    if (!data || typeof data !== "object") return { kind: "unreachable" };
    return { kind: "payload", payload: data };
  } catch {
    return { kind: "unreachable" };
  }
}

// **THE RE-ASK IS FREE, AND THAT IS A PROPERTY OF THE ROUTE RATHER THAN A HOPE:** the handler is a
// single `sendJson` over `resolveEntitlements(readSignedSession(request))` — a read of her session and
// the tier config, no write anywhere. **AND IT DELAYS NO PAINT**, because every consumer already draws
// at its own empty value from the first frame and corrects late; the retry is purely additive and the
// only thing it can change is how often the wrong conclusion sticks.
//
// **THE BOUND IS THE DELAY ROSTER ITSELF (R31)**, so the attempt count is `delays.length + 1` by
// construction and there is no second number that can drift from the budget. R90 paid for that lesson
// one read over: a loop bounded only by `unreachable` never reaches the honest answer at all.
//
// The budget matches R90's deliberately — this is the same class of question asked at the same moment
// of the same page load, and two different retry budgets for two reads that fail together would be two
// answers to one question about how long this house waits.
export const ENTITLEMENTS_RETRY_DELAYS = [600, 1500];

export async function readEntitlements(
  doFetch: typeof fetch = fetch,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
  delays: number[] = ENTITLEMENTS_RETRY_DELAYS,
): Promise<EntitlementsAnswer> {
  let answer = await readEntitlementsOnce(doFetch);
  for (const wait of delays) {
    if (answer.kind !== "unreachable") return answer;
    await sleep(wait);
    answer = await readEntitlementsOnce(doFetch);
  }
  return answer;
}

// **THE SHAPE `useEntitlements` HANDS ITS THREE CONSUMERS, AND `null` IS A STATE RATHER THAN A
// DEFAULT.** The old hook seeded `{ tier: "free", ... }`, which is not an empty value — it is a
// COMPLETE ANSWER saying she pays nothing and owns nothing, and every consumer read it as one. The
// distinction is R18's and W5-2/R39's third state: nothing has arrived is not the same fact as she has
// none of these.
export type MemberEntitlements = {
  tier: string;
  owner: boolean;
  freeReportTypes: string[];
  features: Record<string, boolean>;
  purchases: string[];
  // EVENT-1 — the gate-drop window, laid over her real entitlements by the server on the way out.
  // **THESE ARE ABSENT ON EVERY ORDINARY DAY**, which is why they are optional rather than
  // defaulted: a banner must be able to tell *no event* from *an event with no time left*.
  eventOpen?: boolean;
  eventSecondsLeft?: number;
  eventEndsAt?: number;
};

// Coercion lives HERE rather than at the hook, because it is the same seven-field read `useEntitlements`
// was doing inline and a second copy of it is exactly what R59 folded one read over. A payload that
// arrived with a field missing is still an answer about the fields it DOES carry — that is R20's
// `unregistered` ruling, and it is deliberately not the same as no payload at all.
export function memberEntitlements(payload: EntitlementsPayload): MemberEntitlements {
  return {
    tier: typeof payload.tier === "string" ? payload.tier : "free",
    owner: Boolean(payload.owner),
    freeReportTypes: Array.isArray(payload.freeReportTypes) ? payload.freeReportTypes : [],
    features: payload.features && typeof payload.features === "object" ? payload.features : {},
    // T1-7: one-time products she owns. Absent until the answer arrives, so a slow network shows the
    // BUY face rather than briefly unlocking something she has not bought.
    purchases: Array.isArray(payload.purchases) ? payload.purchases : [],
    // EVENT-1. Read defensively for R20's reason: a payload with NO window is a real answer about
    // a day with no event, and it must never render as an event that has expired.
    eventOpen: Boolean((payload as { eventOpen?: unknown }).eventOpen),
    eventSecondsLeft: Number((payload as { eventSecondsLeft?: unknown }).eventSecondsLeft) || 0,
    eventEndsAt: Number((payload as { eventEndsAt?: unknown }).eventEndsAt) || 0,
  };
}

// =================================================================================================
// >>> **EVENT-1 — THE COUNTDOWN, AS A PURE LAW.** <<<
//
// The banner's whole brain, out here so a .mjs gate can RUN it (the src/lib entry requirement: it
// imports nothing but types) and so the words a member reads are decided in ONE place rather than
// inside a component.
//
// >>> **IT COUNTS DOWN FROM THE END INSTANT, NEVER FROM A TICKING NUMBER.** <<< A client that
// decremented a counter would drift, and a tab left open would drift badly. Every render subtracts
// the real clock from the end the server sent, so a phone that slept for ten minutes wakes correct.
// It is the same reason the server stores an end rather than a flag.
// =================================================================================================
// **THE PARAMETER IS THE TWO FIELDS AND NOT THE WHOLE PAYLOAD**, so the live banner — which polls
// the window route and has no entitlements object at all — asks the SAME law rather than growing a
// second countdown of its own (R28/R29). A full `MemberEntitlements` still satisfies it.
export function eventCountdown(
  ent: Pick<MemberEntitlements, "eventOpen" | "eventEndsAt"> | null,
  now: number = Date.now(),
): { open: boolean; secondsLeft: number; label: string } {
  const shut = { open: false, secondsLeft: 0, label: "" };
  if (!ent || !ent.eventOpen) return shut;
  const endsAt = Number(ent.eventEndsAt) || 0;
  // No end means no countdown. **NOT an open house with an unknown end** — the server always sends
  // the end when it says open, so a payload missing one is one this law will not act on.
  if (!endsAt) return shut;
  const left = Math.max(0, Math.ceil((endsAt - now) / 1000));
  if (left <= 0) return shut;
  const m = Math.floor(left / 60);
  const s = left % 60;
  return {
    open: true,
    secondsLeft: left,
    // Minutes while there is time, seconds at the end — *1 minute left* held for sixty seconds
    // reads as frozen, and the last minute is the one she is actually watching.
    label: m >= 1
      ? String(m) + " minute" + (m === 1 ? "" : "s") + " left"
      : String(s) + " second" + (s === 1 ? "" : "s") + " left",
  };
}
